import {updateHTML} from './updateHTML';
import {attachOnErrorListenerToScript} from './attachOnErrorListenerToScript';

// Source: https://mimesniff.spec.whatwg.org/#mime-type-groups
const VALID_JS_MIME_TYPES = [
  'application/ecmascript',
  'application/javascript',
  'application/x-ecmascript',
  'application/x-javascript',
  'text/ecmascript',
  'text/javascript',
  'text/javascript1.0',
  'text/javascript1.1',
  'text/javascript1.2',
  'text/javascript1.3',
  'text/javascript1.4',
  'text/javascript1.5',
  'text/jscript',
  'text/livescript',
  'text/x-ecmascript',
  'text/x-javascript',
];

/**
 * This function tries to inline the script by downloading it synchronously and replacing the script tag with the downloaded content.
 * If the script fails to download, it will be left as is.
 */
export const tryToInlineScript = ({
  scriptTag,
  doc,
  updatedHtml,
}: {
  scriptTag: HTMLScriptElement;
  doc: Document;
  updatedHtml: string;
}): string => {
  try {
    // Download the script synchronously
    const xhr = new XMLHttpRequest();
    xhr.open('GET', scriptTag.src, false); // false means synchronous
    xhr.send(null);

    // Check response content type (checking that response is valid JS) before attempting to inline script
    const contentType = xhr.getResponseHeader('Content-Type');
    if (
      contentType &&
      !VALID_JS_MIME_TYPES.some((type) => contentType.startsWith(type))
    ) {
      return updatedHtml; // Skip this script
    }

    // Create a new script element
    const newScript = doc.createElement('script');
    newScript.textContent = xhr.responseText;

    // Copy other attributes to the new script
    Array.from(scriptTag.attributes).forEach((attr) => {
      if (attr.name !== 'src') {
        // We don't need to copy 'src'
        newScript.setAttribute(attr.name, attr.value);
      }
    });

    // Replace the old script tag with the new one
    return updateHTML(updatedHtml, scriptTag.src, newScript.outerHTML);
  } catch (error) {
    // if there was an error while attempting to download the script, we will add an error listener to the script tag only
    console.error('Error fetching script:', error);

    return attachOnErrorListenerToScript({
      html: updatedHtml,
      scriptTag,
      doc,
    });
  }
};
